/**
 * Utility functions for handling API error responses.
 *
 * NEW (structured):  parseApiError, getErrorMessage, showApiError
 * LEGACY (kept for backward compat): extractErrorMessage, setErrorSafely, handleApiError
 */

import i18n from 'i18next';
import { translateBackendMessage } from './backendMessages';

// ---------------------------------------------------------------------------
// NEW — structured error handling
// ---------------------------------------------------------------------------

/**
 * Parse an API error response body into a normalized object.
 * Handles both the new `{ error_code, message, details }` envelope
 * and the legacy `{ detail: "string" | { message } }` format.
 *
 * @param {object} responseData - Parsed JSON body from the error response
 * @returns {{ error_code: string|null, message: string, details: object }}
 */
export function parseApiError(responseData) {
  if (!responseData || typeof responseData !== 'object') {
    return { error_code: null, message: String(responseData || ''), details: {} };
  }

  // New structured format: { error_code, message, details }
  if (responseData.error_code) {
    return {
      error_code: responseData.error_code,
      message: responseData.message || '',
      details: responseData.details || {},
    };
  }

  // Legacy FastAPI format: { detail: ... }
  if (responseData.detail) {
    // { detail: "string" }
    if (typeof responseData.detail === 'string') {
      return { error_code: null, message: responseData.detail, details: {} };
    }

    // { detail: [{ msg, loc }] } — Pydantic validation
    if (Array.isArray(responseData.detail)) {
      const first = responseData.detail[0];
      return {
        error_code: 'VALIDATION_ERROR',
        message: first?.msg || 'Validation error',
        details: { errors: responseData.detail },
      };
    }

    // { detail: { error, message, ... } } — old sales_orders format
    if (typeof responseData.detail === 'object') {
      return {
        error_code: responseData.detail.error || responseData.detail.error_code || null,
        message: responseData.detail.message || responseData.detail.msg || '',
        details: responseData.detail.details || {},
      };
    }
  }

  // Fallback — try common fields
  return {
    error_code: responseData.error || null,
    message: responseData.message || responseData.msg || '',
    details: {},
  };
}

/**
 * Map a parsed error to a user-facing i18n message.
 *
 * Looks up `common:errors.{ERROR_CODE}` with the error's details as
 * interpolation values. Falls back to the raw `message` if no i18n key exists.
 *
 * @param {{ error_code: string|null, message: string, details: object }} parsed
 * @param {Function} t - i18next `t` function (bound to `common` namespace)
 * @returns {string}
 */
export function getErrorMessage(parsed, t) {
  if (!parsed.error_code || !t) {
    return translateBackendMessage(parsed.message) || t?.('errors.INTERNAL_SERVER_ERROR') || 'An unexpected error occurred';
  }

  const i18nKey = `errors.${parsed.error_code}`;
  // Merge details + message so interpolation like {{message}} works.
  // `value` is the offending value of `details.field` (e.g. product_code for DUPLICATE_CODE).
  const details = parsed.details || {};
  const message = translateBackendMessage(parsed.message);
  const interpolation = { ...details, value: details[details.field || 'value'], message };
  const translated = t(i18nKey, interpolation);

  // If i18next returns the key itself, the key doesn't exist — fall back to raw message
  if (translated === i18nKey) {
    return message || t('errors.INTERNAL_SERVER_ERROR');
  }
  return translated;
}

/**
 * One-shot error handler: parse response → translate → show toast.
 *
 * @param {Response} response - The fetch Response object (will be .json()-ed)
 * @param {Function} showError - Toast function, signature: showError(title, message)
 * @param {Function} t - i18next `t` function bound to `common` namespace
 * @param {string} [fallbackTitle] - Title for the toast (defaults to i18n genericTitle)
 * @returns {Promise<{ error_code: string|null, message: string, details: object }>}
 */
export async function showApiError(response, showError, t, fallbackTitle) {
  const title = fallbackTitle || t?.('errors.genericTitle') || 'Error';
  try {
    const data = await response.json();
    const parsed = parseApiError(data);
    const message = getErrorMessage(parsed, t);
    showError(title, message);
    return parsed;
  } catch {
    const fallbackMsg = t?.('errors.INTERNAL_SERVER_ERROR') || 'An unexpected error occurred';
    showError(title, fallbackMsg);
    return { error_code: null, message: fallbackMsg, details: {} };
  }
}

// ---------------------------------------------------------------------------
// LEGACY — kept for backward compatibility during migration
// ---------------------------------------------------------------------------

/**
 * @deprecated Use parseApiError + getErrorMessage instead.
 * Extracts a readable error message from various error response formats.
 */
export function extractErrorMessage(errorData, fallbackMessage = 'An unexpected error occurred') {
  return translateBackendMessage(rawErrorMessage(errorData, fallbackMessage));
}

function rawErrorMessage(errorData, fallbackMessage) {
  if (typeof errorData === 'string') {
    return errorData;
  }
  if (errorData instanceof Error) {
    return errorData.message;
  }
  if (errorData && typeof errorData === 'object') {
    if (errorData.detail) {
      if (typeof errorData.detail === 'string') {
        return errorData.detail;
      }
      if (Array.isArray(errorData.detail) && errorData.detail.length > 0) {
        const firstError = errorData.detail[0];
        if (firstError && firstError.msg) {
          return firstError.msg;
        }
        return 'Validation error';
      }
      if (typeof errorData.detail === 'object') {
        if (errorData.detail.message) return errorData.detail.message;
        if (errorData.detail.msg) return errorData.detail.msg;
      }
    }
    // Also handle new structured format for backward compat
    if (errorData.error_code && errorData.message) {
      return errorData.message;
    }
    if (errorData.message) return errorData.message;
    if (errorData.msg) return errorData.msg;
    if (errorData.error) return rawErrorMessage(errorData.error, fallbackMessage);
  }
  return fallbackMessage;
}

/**
 * @deprecated Use showApiError instead.
 * Safely handles error state setting to ensure only strings are stored.
 */
export function setErrorSafely(setError, errorData, fallbackMessage = 'An error occurred') {
  const message = extractErrorMessage(errorData, fallbackMessage);
  setError(message);
}

/**
 * @deprecated Use showApiError instead.
 * Processes API response errors and shows user-friendly messages.
 */
export async function handleApiError(response, showError, fallbackMessage = 'Operation failed') {
  try {
    const errorData = await response.json();
    const message = extractErrorMessage(errorData, fallbackMessage);
    showError(i18n.t('common:errors.genericTitle'), message);
  } catch {
    showError(i18n.t('common:errors.genericTitle'), fallbackMessage);
  }
}